<template>
  <section class="section section-observed portal-list-page">
    <div class="portal-list-panel">
      <div class="portal-list-panel__head">
        <strong>{{ columnMeta.label }}</strong>
        <span>共 {{ total }} 条</span>
      </div>

      <div v-if="loading" class="muted-copy panel-empty">正在加载...</div>
      <div v-else-if="!items.length" class="empty-panel">
        <p class="muted-copy">暂无已发布的{{ columnMeta.label }}内容</p>
      </div>

      <div v-else-if="isPublications" class="portal-cover-grid">
        <button
          v-for="item in items"
          :key="item.entryId || item.title"
          type="button"
          class="portal-cover-card"
          @click="openItem(item)"
        >
          <div class="portal-cover-card__media" aria-hidden="true">
            <img
              v-if="coverUrl(item)"
              :src="coverUrl(item)"
              :alt="item.title || ''"
            >
            <div v-else class="portal-cover-card__empty">无封面</div>
          </div>
          <strong class="portal-cover-card__title">{{ item.title }}</strong>
        </button>
      </div>

      <div v-else class="portal-dated-table" role="table" :aria-label="columnMeta.label + '列表'">
        <div class="portal-dated-table__head" role="row">
          <span role="columnheader">序号</span>
          <span role="columnheader">标题</span>
          <span role="columnheader">日期</span>
        </div>
        <button
          v-for="(item, index) in items"
          :key="item.entryId || item.title"
          type="button"
          class="portal-dated-table__row"
          role="row"
          @click="openItem(item)"
        >
          <span class="portal-dated-table__index" role="cell">{{ itemIndex(index) }}</span>
          <span class="portal-dated-table__title" role="cell">{{ item.title }}</span>
          <time class="portal-dated-table__date" role="cell">[{{ item.date || '—' }}]</time>
        </button>
      </div>

      <FrontPagination
        :page-num="pageNum"
        :page-size="pageSize"
        :total="total"
        @change="onPageChange"
      />
    </div>
  </section>
</template>

<script>
import { listPublicCmsEntries, unwrapPublicPage } from '../../api/paleo'
import FrontPagination from '../../components/common/FrontPagination.vue'

const COLUMN_META = {
  publications: {
    label: '出版物',
    columnCode: 'publications',
    pageSize: 5
  },
  dynamics: {
    label: '学会动态',
    columnCode: 'dynamics',
    pageSize: 5
  },
  progress: {
    label: '工作进展',
    columnCode: 'progress',
    pageSize: 5
  }
}

export default {
  name: 'HomePortalListPage',
  components: { FrontPagination },
  inject: ['front'],
  data() {
    return {
      loading: false,
      items: [],
      pageNum: 1,
      total: 0,
      loadSeq: 0
    }
  },
  computed: {
    columnKey() {
      return (this.$route.meta && this.$route.meta.portalColumn) || 'publications'
    },
    columnMeta() {
      return COLUMN_META[this.columnKey] || COLUMN_META.publications
    },
    isPublications() {
      return this.columnKey === 'publications'
    },
    pageSize() {
      return this.columnMeta.pageSize
    }
  },
  watch: {
    columnKey: {
      immediate: true,
      handler() {
        this.pageNum = 1
        this.loadList()
      }
    }
  },
  methods: {
    itemIndex(index) {
      return (this.pageNum - 1) * this.pageSize + index + 1
    },
    coverUrl(item) {
      const url = item.coverImageUrl || item.coverUrl
      if (!url) {
        return ''
      }
      return this.front.resolvePublicDisplayUrl
        ? this.front.resolvePublicDisplayUrl(url)
        : this.front.resolveFileUrl(url)
    },
    onPageChange(page) {
      this.pageNum = page
      this.loadList()
      if (typeof window !== 'undefined' && window.scrollTo) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    async loadList() {
      const seq = ++this.loadSeq
      this.loading = true
      try {
        const result = await listPublicCmsEntries({
          moduleCode: 'home_portal',
          columnCode: this.columnMeta.columnCode,
          pageNum: this.pageNum,
          pageSize: this.pageSize
        })
        if (seq !== this.loadSeq) return
        const { rows, total } = unwrapPublicPage(result, {
          pageNum: this.pageNum,
          pageSize: this.pageSize
        })
        this.items = (rows || []).map(row => this.front.mapCmsEntry(row))
        this.total = total || 0
      } catch (error) {
        if (seq !== this.loadSeq) return
        if (!this.items.length) {
          this.items = []
          this.total = 0
        }
      } finally {
        if (seq === this.loadSeq) this.loading = false
      }
    },
    openItem(item) {
      if (!item) return
      const url = String(item.linkUrl || '').trim()
      if (/^https?:\/\//i.test(url)) {
        window.open(url, '_blank', 'noopener')
        return
      }
      if (url.startsWith('/')) {
        this.front.goPath(url)
        return
      }
      this.front.openContentDetail(item)
    }
  }
}
</script>

<style scoped>
.portal-list-page {
  padding-top: 8px;
}

.portal-list-panel {
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 8px;
  background: #fff;
  padding: 20px 22px 24px;
}

.portal-list-panel__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(31, 79, 122, 0.12);
}

.portal-list-panel__head strong {
  color: var(--primary, #002b49);
  font-size: 18px;
  font-weight: 800;
}

.portal-list-panel__head span,
.panel-empty,
.muted-copy {
  color: var(--subtle, #6b7280);
  font-size: 13px;
}

.empty-panel {
  padding: 28px 8px;
}

.portal-cover-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(112px, 120px));
  justify-content: start;
  gap: 20px 22px;
  padding: 4px 0 8px;
}

.portal-cover-card {
  display: flex;
  flex-direction: column;
  width: 120px;
  margin: 0;
  padding: 0;
  border: 1px solid rgba(31, 79, 122, 0.12);
  border-radius: 4px;
  background: #fff;
  overflow: hidden;
  text-align: center;
  cursor: pointer;
  color: inherit;
  font: inherit;
  box-shadow: 0 1px 3px rgba(0, 29, 54, 0.06);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.portal-cover-card:hover {
  border-color: var(--gold, #c9a227);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 29, 54, 0.1);
}

.portal-cover-card__media {
  background: #f0f2f5;
  line-height: 0;
}

.portal-cover-card__media img,
.portal-cover-card__empty {
  display: block;
  width: 100%;
  aspect-ratio: 5 / 7;
  height: auto;
  object-fit: contain;
  background: #f0f2f5;
}

.portal-cover-card__empty {
  display: grid;
  place-items: center;
  color: var(--subtle, #6b7280);
  font-size: 12px;
  line-height: 1.4;
}

.portal-cover-card__title {
  display: block;
  padding: 8px 6px;
  color: var(--primary, #002b49);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
  background: #f7f8fa;
  border-top: 1px solid rgba(31, 79, 122, 0.08);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.portal-dated-table {
  display: grid;
}

.portal-dated-table__head,
.portal-dated-table__row {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr) 140px;
  gap: 12px;
  align-items: center;
  width: 100%;
}

.portal-dated-table__head {
  padding: 10px 8px;
  border-bottom: 2px solid rgba(31, 79, 122, 0.14);
  color: var(--primary, #002b49);
  font-size: 13px;
  font-weight: 800;
}

.portal-dated-table__row {
  margin: 0;
  padding: 14px 8px;
  border: 0;
  border-bottom: 1px solid rgba(31, 79, 122, 0.1);
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.portal-dated-table__row:hover .portal-dated-table__title {
  color: #c41e3a;
}

.portal-dated-table__index {
  color: var(--subtle, #6b7280);
  font-size: 13px;
}

.portal-dated-table__title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1a1a1a;
  font-size: 15px;
  line-height: 1.5;
}

.portal-dated-table__date {
  justify-self: end;
  color: #8a93a0;
  font-size: 13px;
  font-style: normal;
}

@media (max-width: 900px) {
  .portal-cover-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 112px));
    gap: 16px;
  }

  .portal-cover-card {
    width: 112px;
  }

  .portal-dated-table__head,
  .portal-dated-table__row {
    grid-template-columns: 48px minmax(0, 1fr) 110px;
  }
}

@media (max-width: 560px) {
  .portal-cover-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }

  .portal-cover-card {
    width: auto;
  }

  .portal-dated-table__head {
    display: none;
  }

  .portal-dated-table__row {
    grid-template-columns: 36px minmax(0, 1fr);
    grid-template-rows: auto auto;
  }

  .portal-dated-table__date {
    grid-column: 2;
    justify-self: start;
  }
}
</style>
